import { HttpHeaders } from '@angular/common/http'
import { Injectable, inject } from '@angular/core'

import { Store } from '@ngrx/store'
import { omit as _omit, pick as _pick } from 'lodash/fp'

import { Observable, map } from 'rxjs'

import { PaginatedResponse, ProcessDataModel } from '@flowx/core-sdk'
import {
  CreateResourceDataSlicesPayload,
  CreateReusableUiTemplateDataMapperPayload,
  ResourceDataSlice,
  ReusableUiTemplateDataMapper,
  ReusableUiTemplateDataMappingResponse,
  UpdateResourceDataSlicesPayload,
  UpdateReusableUiTemplateDataMapperPayload,
} from '@flowx/designer/modules/core/dictionary/data-mapping.dictionary'
import { DataModelEntity } from '@flowx/designer/modules/core/dictionary/data-model.dictionary'
import {
  CopyTemplateConfigResponse,
  PasteTemplateConfigPayload,
  PasteTemplateConfigResponse,
} from '@flowx/designer/modules/core/dictionary/template-config.dictionary'
import { TemplateConfigInterface } from '@flowx/shared/interfaces'
import { addParams } from '@flowx/shared/utils'

import { ApiHttpProxyService, ForwardProxyMicroservice } from './api-http-proxy.service'
import { DEFAULT_PAGINATION_PARAMS, PaginationQueryParams } from './api-http.service'

import {
  AppResource,
  ApplicationResourceType,
  ManifestInfo,
  PROJECT_DATA_TYPE_MAPPING,
  ProxyApiAction,
} from '../entities/application.entity'
import { UpdateTemplateConfigKey } from '../entities/data-model.entity'
import {
  ApplicationReusableTemplateBody,
  HostResourceType,
  ReusableTemplate,
} from '../entities/reusable-resources.entity'
import { selectReusableUiTemplateResourceId } from '../store/selectors/template-config.selectors'

export type ContentMapValue = boolean | number | string

export interface ReusableTemplateParams {
  application: string
  version: number
  draft: boolean
  type: string
}

export interface CreateReusableUiTemplateFromComponentPayload {
  name: string
  description?: string | null
  uiTemplateIds: number[]
  targetAppId: string
  targetAppVersionId: string
  hostResourceType: HostResourceType
}

@Injectable({
  providedIn: 'root',
})
export class ReusableTemplatesApiService extends ApiHttpProxyService {
  #store = inject(Store)
  #url = 'api/reusable-template'

  #resourceId = this.#store.selectSignal(selectReusableUiTemplateResourceId)

  getAllReusableTemplates(
    params: PaginationQueryParams & { name?: string; latest?: boolean }
  ): Observable<
    AppResource<PaginatedResponse<ReusableTemplate>> & { manifestInfo?: ManifestInfo[] }
  > {
    return this.get(
      `${this.#url}/findAll`,
      {
        microservice: ForwardProxyMicroservice.Admin,
        resourceType: ApplicationResourceType.ReusableTemplate,
      },
      {
        params: this.addParams({
          ...DEFAULT_PAGINATION_PARAMS,
          ...params,
          includeManifestInfo: true,
        }),
      }
    )
  }

  createReusableTemplate(
    payload: ApplicationReusableTemplateBody
  ): Observable<AppResource<ReusableTemplate>> {
    return this.post(
      this.#url,
      {
        action: ProxyApiAction.CREATE,
        resourceName: payload.name,
        payload,
      },
      {
        microservice: ForwardProxyMicroservice.Admin,
        resourceType: ApplicationResourceType.ReusableTemplate,
      }
    )
  }

  updateReusableTemplate(
    payload: Omit<ApplicationReusableTemplateBody, 'hostResourceType'>,
    resourceId: string
  ): Observable<any> {
    return this.patch(this.#url, payload, {
      resourceName: payload.name,
      microservice: ForwardProxyMicroservice.Admin,
      resourceType: ApplicationResourceType.ReusableTemplate,
      resourceId,
      action: ProxyApiAction.UPDATE,
    })
  }

  deleteReusableTemplate(resourceId: string): Observable<void> {
    return this.delete(
      this.#url,
      {
        action: ProxyApiAction.DELETE,
        params: { force: true },
      },
      {
        microservice: ForwardProxyMicroservice.Admin,
        resourceType: ApplicationResourceType.ReusableTemplate,
        resourceId,
      }
    )
  }

  // Reusable Templates DESIGNER API

  getReusableTemplateResource(
    appId: string,
    appVersionId: string,
    resourceId: string
  ): Observable<AppResource<ReusableTemplate>> {
    return this.httpClient.get<AppResource<ReusableTemplate>>(
      `${this.getBaseUrl()}/appmanager/api/resource/app/${appId}/app-version/${appVersionId}/rt/reusable-template/ri/${resourceId}/admin/api/reusable-template/getById`
    )
  }

  getReusableTemplateById(resourceId: string): Observable<AppResource<ReusableTemplate>> {
    return this.get(`${this.#url}/getById`, {
      microservice: ForwardProxyMicroservice.Admin,
      resourceType: ApplicationResourceType.ReusableTemplate,
      resourceId,
    })
  }

  getReusableTemplate(resourceId: string): Observable<AppResource<ReusableTemplate>> {
    return this.get(`${this.#url}/templates`, {
      microservice: ForwardProxyMicroservice.Admin,
      resourceType: ApplicationResourceType.ReusableTemplate,
      resourceId,
    })
  }

  createNodeTemplate(
    resourceId: number | string,
    template: Partial<TemplateConfigInterface>
  ): Observable<
    AppResource<{ template: TemplateConfigInterface; reordered: Record<number, number> }>
  > {
    return this.patch(`${this.#url}/templates`, template, {
      action: ProxyApiAction.CREATE,
      microservice: ForwardProxyMicroservice.Admin,
      resourceType: ApplicationResourceType.ReusableTemplate,
      resourceId: resourceId as string,
    })
  }

  deleteTemplateConfigs(
    templateConfigs: TemplateConfigInterface[]
  ): Observable<TemplateConfigInterface[]> {
    return this.patch(
      `${this.#url}/templates/bulk`,
      {},
      {
        action: ProxyApiAction.DELETE,
        microservice: ForwardProxyMicroservice.Admin,
        resourceType: ApplicationResourceType.ReusableTemplate,
        resourceId: this.#resourceId(),
      },
      {
        params: addParams({ ids: templateConfigs.map(({ id }) => id) }),
      }
    )
  }

  bulkUpdateTemplateConfigProperties(
    templateConfigs: TemplateConfigInterface[]
  ): Observable<TemplateConfigInterface[]> {
    const removeChildrenFromTemplateConfig = (
      templateConfig: TemplateConfigInterface
    ): Omit<TemplateConfigInterface, 'templateConfig'> =>
      _omit(['templateConfig', 'meta'], templateConfig)
    const templateConfigsForSave = templateConfigs.map(removeChildrenFromTemplateConfig)

    return this.patch(`${this.#url}/templates/properties/bulk`, templateConfigsForSave, {
      action: ProxyApiAction.PARTIAL_UPDATE,
      microservice: ForwardProxyMicroservice.Admin,
      resourceType: ApplicationResourceType.ReusableTemplate,
      resourceId: this.#resourceId(),
    }).pipe(map((response: { resourceContent: any }) => response.resourceContent))
  }

  updateTemplateConfigKey(
    payload: UpdateTemplateConfigKey
  ): Observable<AppResource<Partial<TemplateConfigInterface>>> {
    return this.patch(`${this.#url}/templates/key`, payload, {
      action: ProxyApiAction.PARTIAL_UPDATE,
      microservice: ForwardProxyMicroservice.Admin,
      resourceType: ApplicationResourceType.ReusableTemplate,
      resourceId: this.#resourceId(),
      resourceTypesToIncludeMappingFor: PROJECT_DATA_TYPE_MAPPING,
    })
  }

  bulkUpdateTemplateConfigKey(
    payloads: UpdateTemplateConfigKey[]
  ): Observable<AppResource<Partial<TemplateConfigInterface>>[]> {
    return this.patch(
      `${this.#url}/templates/key/bulk`,
      { keys: payloads },
      {
        action: ProxyApiAction.PARTIAL_UPDATE,
        microservice: ForwardProxyMicroservice.Admin,
        resourceType: ApplicationResourceType.ReusableTemplate,
        resourceId: this.#resourceId(),
        resourceTypesToIncludeMappingFor: PROJECT_DATA_TYPE_MAPPING,
      }
    )
  }

  copyTemplates(templateConfigsIds: number[]): Observable<CopyTemplateConfigResponse> {
    return this.get(
      'api/reusable-template/templates/copy',
      {
        microservice: ForwardProxyMicroservice.Admin,
        resourceType: ApplicationResourceType.ReusableTemplate,
        resourceId: this.#resourceId(),
      },
      {
        params: addParams({ ids: templateConfigsIds }),
      }
    ).pipe(map((response) => response.resourceContent))
  }

  pasteTemplates(
    parentTemplateConfigId: number,
    copiedTemplateConfig: PasteTemplateConfigPayload
  ): Observable<PasteTemplateConfigResponse> {
    return this.patch(
      `api/reusable-template/templates/paste/${parentTemplateConfigId}`,
      copiedTemplateConfig,
      {
        action: ProxyApiAction.CREATE,
        microservice: ForwardProxyMicroservice.Admin,
        resourceType: ApplicationResourceType.ReusableTemplate,
        resourceId: this.#resourceId(),
        resourceTypesToIncludeMappingFor: [
          ApplicationResourceType.ProjectDataType,
          ApplicationResourceType.ReusableTemplate,
          ApplicationResourceType.CustomComponent,
        ],
      }
    ).pipe(
      map((response: { resourceContent: PasteTemplateConfigResponse }) => response.resourceContent)
    )
  }

  getReusableUiTemplates(
    hostResourceType: HostResourceType
  ): Observable<AppResource<ReusableTemplate[]>> {
    return this.get(
      `${this.#url}/available-templates`,
      {
        microservice:
          `include-dep-res/${ForwardProxyMicroservice.Admin}` as ForwardProxyMicroservice,
        resourceType: ApplicationResourceType.ReusableTemplate,
      },
      {
        params: this.addParams({ hostResourceType }),
      }
    )
  }

  testReusableTemplate(resourceId: string): Observable<any> {
    return this.get(
      `${this.#url}/test`,
      {
        microservice:
          `with-ids-mappings/${ForwardProxyMicroservice.Admin}` as ForwardProxyMicroservice,
        resourceType: ApplicationResourceType.ReusableTemplate,
        resourceId,
      },
      {
        headers: new HttpHeaders({ 'flowx-platform': 'web' }),
        params: this.addParams({
          resourceTypesToIncludeMappingFor: ApplicationResourceType.CustomComponent,
        }),
      }
    )
  }

  getReusableTemplateDataModel(resourceId: string): Observable<AppResource<ProcessDataModel>> {
    return this.patch(
      `${this.#url}/data-model/data-model-paths`,
      {},
      {
        action: ProxyApiAction.CREATE,
        microservice: ForwardProxyMicroservice.Admin,
        resourceType: ApplicationResourceType.ReusableTemplate,
        resourceTypesToIncludeMappingFor: [ApplicationResourceType.ProjectDataType],
        resourceId,
      }
    )
  }

  createReusableUiTemplateFromComponent(
    payload: CreateReusableUiTemplateFromComponentPayload
  ): Observable<AppResource<ReusableTemplate>> {
    const { targetAppId, targetAppVersionId } = payload
    return this.httpClient.post<AppResource<ReusableTemplate>>(
      `${this.baseUrl}/appmanager/api/resource/app/${targetAppId}/app-version/${targetAppVersionId}/rt/reusable-template/admin/${this.#url}/extract`,
      {
        action: ProxyApiAction.CREATE,
        resourceName: payload.name,
        payload: _pick(
          [
            'name',
            'description',
            'uiTemplateIds',
            'targetAppId',
            'hostResourceType',
          ] satisfies (keyof CreateReusableUiTemplateFromComponentPayload)[],
          payload
        ),
      }
    )
  }

  // Reusable Templates Data Slices

  getDataSlices(resourceId: string): Observable<{ resourceContent: ResourceDataSlice[] }> {
    return this.get(`${this.#url}/data-slice`, {
      microservice: ForwardProxyMicroservice.Admin,
      resourceType: ApplicationResourceType.ReusableTemplate,
      resourceId,
    })
  }

  createDataSlice(
    resourceId: string,
    payload: CreateResourceDataSlicesPayload
  ): Observable<{ resourceContent: ResourceDataSlice }> {
    return this.patch(`${this.#url}/data-slice`, payload, {
      action: ProxyApiAction.CREATE,
      microservice: ForwardProxyMicroservice.Admin,
      resourceType: ApplicationResourceType.ReusableTemplate,
      resourceTypesToIncludeMappingFor: [ApplicationResourceType.ProjectDataType],
      resourceId,
    })
  }

  updateDataSlice(
    resourceId: string,
    payload: UpdateResourceDataSlicesPayload
  ): Observable<{ resourceContent: ResourceDataSlice }> {
    return this.patch(`${this.#url}/data-slice`, payload, {
      action: ProxyApiAction.UPDATE,
      microservice: ForwardProxyMicroservice.Admin,
      resourceType: ApplicationResourceType.ReusableTemplate,
      resourceTypesToIncludeMappingFor: [ApplicationResourceType.ProjectDataType],
      resourceId,
    })
  }

  getDataModelEntitiesAndDataSlices(flowxUuid: string): Observable<{
    resourceContent: {
      dataEntities: DataModelEntity[]
      slices: ResourceDataSlice[]
    }
  }> {
    return this.get(
      `api/process/reusable-template/${flowxUuid}/data-slices`,
      {
        microservice:
          `with-ids-mappings/${ForwardProxyMicroservice.Admin}` as ForwardProxyMicroservice,
        resourceType: ApplicationResourceType.Process,
      },
      {
        params: {
          resourceTypesToIncludeMappingFor: [ApplicationResourceType.ReusableTemplate],
        },
      }
    )
  }

  getMappings(
    flowxUuid: string
  ): Observable<{ resourceContent: ReusableUiTemplateDataMappingResponse }> {
    return this.get(`api/process/reusable-template/${flowxUuid}/mappings`, {
      microservice: ForwardProxyMicroservice.Admin,
      resourceType: ApplicationResourceType.Process,
    })
  }

  createMapping(
    flowxUuid: string,
    payload: CreateReusableUiTemplateDataMapperPayload
  ): Observable<{ resourceContent: ReusableUiTemplateDataMapper }> {
    return this.patch(`api/process/reusable-template/${flowxUuid}/mappings`, payload, {
      action: ProxyApiAction.CREATE,
      microservice: ForwardProxyMicroservice.Admin,
      resourceType: ApplicationResourceType.Process,
      resourceTypesToIncludeMappingFor: [
        ApplicationResourceType.ProjectDataType,
        ApplicationResourceType.Process,
        ApplicationResourceType.ReusableTemplate,
      ],
    })
  }

  updateMapping(
    flowxUuid: string,
    payload: UpdateReusableUiTemplateDataMapperPayload
  ): Observable<{ resourceContent: ReusableUiTemplateDataMapper }> {
    return this.patch(`api/process/reusable-template/${flowxUuid}/mappings`, payload, {
      action: ProxyApiAction.UPDATE,
      microservice: ForwardProxyMicroservice.Admin,
      resourceType: ApplicationResourceType.Process,
      resourceTypesToIncludeMappingFor: [
        ApplicationResourceType.ProjectDataType,
        ApplicationResourceType.Process,
        ApplicationResourceType.ReusableTemplate,
      ],
    })
  }
}
